import * as React from 'react'

import { runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { RouteComponentProps } from 'react-router'

import NavBar from '~/client/src/desktop/components/NavBar/NavBar'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { withErrorBoundary } from '~/client/src/shared/components/ErrorBoundary'
import FeatureFlag from '~/client/src/shared/components/FeatureFlag'
import * as Layout from '~/client/src/shared/components/Layout'
import { Loader } from '~/client/src/shared/components/Loader'
import FlaggedFeatures from '~/client/src/shared/enums/FlaggedFeatures'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import isTestEnvironment from '~/client/src/shared/utils/environment'

import GeneralMapViewSetUp from '../../components/GeneralMapViewSetUp/GeneralMapViewSetUp'
import DesktopInitialState from '../../stores/DesktopInitialState'
import UnsafeUserDirectoryView from '../UserDirectory'
import UnsafeAdminSummary from './components/AdminSummary/AdminSummary'
import UnsafeAnalyticsSetup from './components/AnalyticsSetup/AnalyticsSetup'
import UnsafeIntegrationsSetUp from './components/AppsIntegrations/IntegrationsSetUp'
import UnsafeCompaniesDirectory from './components/CompaniesDirectory/CompaniesDirectory'
import UnsafeDeliveryCardsSetUp from './components/DeliveryCalendarSetUp/DeliveryCardsSetUp'
import UnsafeDeliveryRestrictions from './components/DeliveryRestrictions/DeliveryRestrictions'
import EquipmentSetup from './components/Equipment/EquipmentSetup'
import LocationsSetup from './components/Locations/LocationsSetup'
import MaterialResourcesSetup from './components/MaterialResources/MaterialResourcesSetup'
import UnsafeMaterialsUpload from './components/MaterialsUpload/MaterialsUpload'
import NotificationsSettings from './components/NotificationsSettings/NotificationsSettings'
import UnsafeNotificationsSetup, {
  NotificationSetupModes,
} from './components/NotificationsSetup/NotificationsSetup'
import UnsafePermitsSetUp from './components/PermitsSetUp/PermitsSetUp'
import UnsafePresentationMode from './components/PresentationMode/PresentationMode'
import UnsafeProfile from './components/Profile/Profile'
import UnsafeProjectCreation from './components/ProjectCreation/ProjectCreation'
import UnsafeProjectWorkflowsSetUp from './components/ProjectWorkflowsSetUp/DeliveryWorkflows'
import UnsafeProjectWorkspaceSetUp from './components/ProjectWorkspaceSetUp/ProjectWorkspaceSetUp'
import UnsafeQRCodes from './components/QRCodes/QRCodes'
import SetUpNavigation from './components/SetUpSteps/SetUpNavigation'
import UnsafeTagsDirectory from './components/TagsDirectory/TagsDirectory'
import ProjectSetUpPageStore from './ProjectSetUpPage.store'
import ProjectSetUpPage from './ProjectSetUpPages'

import './ProjectSetUp.scss'

const AdminSummary = withErrorBoundary(UnsafeAdminSummary)
const UserDirectoryView = withErrorBoundary(UnsafeUserDirectoryView)
const QRCodes = withErrorBoundary(UnsafeQRCodes)
const ProjectWorkflowsSetUp = withErrorBoundary(UnsafeProjectWorkflowsSetUp)
const ProjectWorkspaceSetUp = withErrorBoundary(UnsafeProjectWorkspaceSetUp)
const PermitsSetUp = withErrorBoundary(UnsafePermitsSetUp)
const PresentationMode = withErrorBoundary(UnsafePresentationMode)
const IntegrationsSetUp = withErrorBoundary(UnsafeIntegrationsSetUp)
const NotificationsSetup = withErrorBoundary(UnsafeNotificationsSetup)
const AnalyticsSetup = withErrorBoundary(UnsafeAnalyticsSetup)
const CompaniesDirectory = withErrorBoundary(UnsafeCompaniesDirectory)
const TagsDirectory = withErrorBoundary(UnsafeTagsDirectory)
const DeliveryRestrictions = withErrorBoundary(UnsafeDeliveryRestrictions)
const DeliveryCardsSetUp = withErrorBoundary(UnsafeDeliveryCardsSetUp)
const ProjectCreation = withErrorBoundary(UnsafeProjectCreation)
const MaterialsUpload = withErrorBoundary(UnsafeMaterialsUpload)

interface IProjectSetUpParams {
  projectsStore?: ProjectsStore
  eventsStore?: DesktopEventStore
  projectSetUpPageStore?: ProjectSetUpPageStore
  globeViewsStore?: GlobeViewsStore

  // If true, only the sidebar will be rendered. No routing props are required then.
  sidebarOnly?: boolean
  className?: string
}

// Allow using the component without routing props for sidebarOnly mode
type OptionalRouteProps = Partial<RouteComponentProps<any>>

const flaggedFeatures = [
  {
    feature: FlaggedFeatures.PROJECT_CREATION,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.TRACKER_PROJECT_TYPE_TOGGLING,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.DELIVERIES_PROJECT_TYPE_TOGGLING,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.FORMS_PROJECT_TYPE_TOGGLING,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.LOGISTICS_PROJECT_TYPE_TOGGLING,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.MATERIALS_PROJECT_TYPE_TOGGLING,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.ALLOW_PHOTOS,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.ALLOW_ANALYTICS,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.ALLOW_TILESETS,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.ALLOW_EQUIPMENT,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.ALLOW_STRUX_TAGS,
    defaultValue: true,
  },
  {
    feature: FlaggedFeatures.MATERIAL_RESOURCES_TOGGLING,
    defaultValue: true,
  },
  // Appended, never inserted: renderFlaggedContent destructures the resolved booleans
  // positionally, so a new entry anywhere but the end silently renames every flag after it.
  {
    feature: FlaggedFeatures.ALLOW_LOCATIONS,
    defaultValue: true,
  },
]

const profilePageRelatedEvents = [
  e.GET_CURRENT_USER,
  e.GET_AUTH_USER_PROJECT,
  e.LOAD_AND_LISTEN_TO_COMPANIES,
  e.LOAD_AND_LISTEN_TO_PROJECT_ROLES,
]

const integrationsPageRelatedEvents = [
  e.INIT_PROJECT_INTEGRATIONS,
  e.GET_MATURIX_CONFIGURATION,
]

/**
 * Saves a settings form fires as the user edits, rather than page loads, so
 * `isPageLoading` must ignore them. Swapping the page for a Loader mid-edit
 * unmounts the form and the store behind it, which reads as the page reloading
 * and silently discards any state that only lives there (STX1-983: the
 * schedule's Everyday/Custom mode was reset on every save).
 *
 * A deny-list is the wrong shape long-term — this is its second entry from a
 * second ticket, and SAVE_PROJECT_ADDRESSES / SAVE_PROJECT_TYPE_OPTIONS still
 * blank the page. The fix is to gate the Loader on "this page has no data yet"
 * instead of enumerating exemptions; that is a wider change than STX1-983.
 */
const EDIT_TIME_SAVE_EVENTS = new Set<string>([
  e.SAVE_MANY_OPERATION_RULES,
  e.DELETE_MANY_OPERATION_RULES,
  e.SAVE_PROJECT,
  // A per-user save (role/tag/admin change) shouldn't blank the whole setup
  // page behind a spinner — keep the page interactive during it.
  e.SAVE_USER_PROJECTS,
])

const pagesWithoutPadding = [
  ProjectSetUpPage.PROJECT_CREATION,
  ProjectSetUpPage.MAP_SETUP,
  ProjectSetUpPage.FORM_WORKFLOWS,
  ProjectSetUpPage.DELIVERY_CARDS,
  ProjectSetUpPage.USER_DIRECTORY,
  ProjectSetUpPage.COMPANY_DIRECTORY,
  ProjectSetUpPage.MATERIALS,
  ProjectSetUpPage.EQUIPMENT,
  ProjectSetUpPage.LOCATIONS,
]

@inject(
  'projectsStore',
  'eventsStore',
  'projectSetUpPageStore',
  'globeViewsStore',
)
@observer
export default class ProjectSetUp extends React.Component<
  IProjectSetUpParams & OptionalRouteProps
> {
  private readonly store: ProjectSetUpPageStore
  private appState: DesktopInitialState = null

  public constructor(props: IProjectSetUpParams & RouteComponentProps<any>) {
    super(props)

    this.appState = props.eventsStore.appState
    this.store = props.projectSetUpPageStore
  }

  public UNSAFE_componentWillMount() {
    const search = this.props.location?.search ?? ''
    this.store.applyPageFromQueryParams(search)
  }

  public UNSAFE_componentWillReceiveProps(
    nextProps: Readonly<IProjectSetUpParams & OptionalRouteProps>,
  ) {
    const search = nextProps.location?.search ?? ''
    this.store.applyPageFromQueryParams(search)
  }

  private get isPageLoading(): boolean {
    return Array.from(this.appState.loading.entries()).some(
      ([key, value]) => value && !EDIT_TIME_SAVE_EVENTS.has(key),
    )
  }

  public componentDidMount() {
    const { projectsStore } = this.props
    projectsStore.loadProjectsList()
  }

  public componentWillUnmount(): void {
    const { projectSetUpPageStore } = this.props

    // Ensure the sidebar is back in sync when leaving the settings
    runInAction(() => {
      projectSetUpPageStore.isSettingsSidebarCollapsed =
        projectSetUpPageStore.isSharedSidebarCollapsed
    })
  }

  public render() {
    const { className } = this.props
    const { isAdmin } = this.store

    if (this.props.sidebarOnly) {
      if (!this.store.showSidebarOutsideOfSettings || !this.isAdmin)
        return <div className="col no-grow sidebar-hidden"></div>

      return (
        <SetUpNavigation
          sidebarOnly
          projectSetUpPageStore={this.store}
          className={classList({
            [className]: !!className,
            'sidebar-expanded': !this.isSidebarCollapsed,
            'sidebar-collapsed': this.isSidebarCollapsed,
          })}
        />
      )
    }

    const shouldShowAllSettings = isAdmin
    return (
      <Layout.View className={this.props.className}>
        <Layout.Header className="layout-navigation">
          <NavBar currentSettingsPage={this.page} />
        </Layout.Header>
        <Layout.Content>
          <div className="project-setup-container">
            <SetUpNavigation projectSetUpPageStore={this.store} />
            <div
              className={classList({
                'project-setup-mainframe': true,
                pl20: !pagesWithoutPadding.includes(this.page),
                'overflow-auto':
                  this.page !== ProjectSetUpPage.MAP_SETUP &&
                  this.page !== ProjectSetUpPage.DELIVERY_CARDS,
                'overflow-hidden':
                  this.page === ProjectSetUpPage.MAP_SETUP ||
                  this.page === ProjectSetUpPage.DELIVERY_CARDS,
              })}
            >
              <>
                {this.page === ProjectSetUpPage.DEFAULT && (
                  <AdminSummary projectSetUpPageStore={this.store} />
                )}
                {this.page === ProjectSetUpPage.EDIT_PROFILE &&
                  this.renderProfile()}
                {this.page === ProjectSetUpPage.NOTIFICATION_SETTINGS && (
                  <NotificationsSettings />
                )}
                {this.page === ProjectSetUpPage.QR_SCANNERS_SETUP && (
                  <QRCodes projectSetUpPageStore={this.store} />
                )}
              </>
              {shouldShowAllSettings && (
                <>
                  {this.page === ProjectSetUpPage.PROJECT_DETAILS &&
                    this.renderFeatureFlag(this.renderFlaggedContent)}
                  {this.page === ProjectSetUpPage.PROJECT_CREATION &&
                    this.renderFeatureFlag(this.renderProjectCreationContent)}
                  {this.page === ProjectSetUpPage.USER_DIRECTORY && (
                    <UserDirectoryView />
                  )}
                  {this.page === ProjectSetUpPage.COMPANY_DIRECTORY && (
                    <CompaniesDirectory />
                  )}
                  {this.page === ProjectSetUpPage.USER_TAGS && (
                    <TagsDirectory projectSetUpPageStore={this.store} />
                  )}
                  {this.page === ProjectSetUpPage.PRESENTATION_MODE && (
                    <PresentationMode />
                  )}
                  {this.page === ProjectSetUpPage.FORM_WORKFLOWS && (
                    <PermitsSetUp />
                  )}
                  {this.page === ProjectSetUpPage.DELIVERY_WORKFLOWS && (
                    <ProjectWorkflowsSetUp projectSetUpPageStore={this.store} />
                  )}
                  {this.page === ProjectSetUpPage.DELIVERY_LOCATIONS && (
                    <DeliveryRestrictions projectSetUpPageStore={this.store} />
                  )}
                  {this.page === ProjectSetUpPage.DELIVERY_CARDS && (
                    <DeliveryCardsSetUp />
                  )}
                  {this.page === ProjectSetUpPage.DELIVERY_NOTIFICATIONS && (
                    <NotificationsSetup
                      projectSetUpPageStore={this.store}
                      mode={NotificationSetupModes.delivery}
                    />
                  )}
                  {this.page === ProjectSetUpPage.MAP_SETUP &&
                    this.renderMapView()}
                  {this.page === ProjectSetUpPage.INTEGRATIONS &&
                    this.renderIntegrationsSetUp()}
                  {this.page === ProjectSetUpPage.ANALYTICS && (
                    <AnalyticsSetup />
                  )}
                  {this.page === ProjectSetUpPage.MATERIALS_UPLOAD && (
                    <MaterialsUpload projectSetUpPageStore={this.store} />
                  )}
                  {this.page === ProjectSetUpPage.MATERIALS && (
                    <MaterialResourcesSetup />
                  )}
                  {this.page === ProjectSetUpPage.EQUIPMENT && (
                    <EquipmentSetup />
                  )}
                  {this.page === ProjectSetUpPage.LOCATIONS && (
                    <LocationsSetup />
                  )}
                  {this.page === ProjectSetUpPage.FORM_NOTIFICATIONS && (
                    <NotificationsSetup
                      projectSetUpPageStore={this.store}
                      mode={NotificationSetupModes.form}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </Layout.Content>
        <Layout.Footer>
          <div />
        </Layout.Footer>
      </Layout.View>
    )
  }

  private get page() {
    return this.store.currentPage
  }

  private renderFeatureFlag(renderContent) {
    return (
      <FeatureFlag
        features={flaggedFeatures.map(f => f.feature)}
        defaultValues={flaggedFeatures.map(
          f => f.defaultValue ?? isTestEnvironment(),
        )}
        render={renderContent}
      />
    )
  }

  private renderFlaggedContent = ([
    isProjectCreationAvailable,
    isTrackerToggleAvailable,
    isDeliveriesToggleAvailable,
    isFormsToggleAvailable,
    isLogisticsToggleAvailable,
    isMaterialsToggleAvailable,
    isPhotosAvailable,
    isAnalyticsAvailable, // ALLOW_TILESETS - not used in project type settings
    ,
    isEquipmentToggleAvailable,
    isStruxTagsAvailable,
    isMaterialResourcesToggleAvailable,
    isLocationsToggleAvailable,
  ]: boolean[]) => {
    return this.isPageLoading ? (
      <Loader />
    ) : (
      <ProjectWorkspaceSetUp
        projectSetUpPageStore={this.store}
        initProjectId={this.appState.activeProject.id}
        isProjectCreationAvailable={isProjectCreationAvailable}
        isTrackerToggleAvailable={isTrackerToggleAvailable}
        isDeliveriesToggleAvailable={isDeliveriesToggleAvailable}
        isLogisticsToggleAvailable={isLogisticsToggleAvailable}
        isMaterialsToggleAvailable={isMaterialsToggleAvailable}
        isMaterialResourcesToggleAvailable={isMaterialResourcesToggleAvailable}
        isFormsToggleAvailable={isFormsToggleAvailable}
        isPhotosAvailable={isPhotosAvailable}
        isAnalyticsAvailable={isAnalyticsAvailable}
        isEquipmentToggleAvailable={isEquipmentToggleAvailable}
        isStruxTagsAvailable={isStruxTagsAvailable}
        isLocationsToggleAvailable={isLocationsToggleAvailable}
      />
    )
  }

  private renderProjectCreationContent = ([
    isProjectCreationAvailable,
    isTrackerToggleAvailable,
    isDeliveriesToggleAvailable,
  ]: boolean[]) => {
    if (isProjectCreationAvailable) {
      return <ProjectCreation projectSetUpPageStore={this.store} />
    }

    return this.renderFlaggedContent([
      isProjectCreationAvailable,
      isTrackerToggleAvailable,
      isDeliveriesToggleAvailable,
    ])
  }

  private renderProfile(): JSX.Element {
    const { loading } = this.appState
    const isLoading = profilePageRelatedEvents.some(event => loading.get(event))

    return isLoading ? (
      <Loader />
    ) : (
      <UnsafeProfile initProjectId={this.appState.activeProject.id} />
    )
  }

  private renderMapView(): JSX.Element {
    const { isDataReceived } = this.props.globeViewsStore
    return isDataReceived ? (
      <GeneralMapViewSetUp projectSetUpPageStore={this.store} />
    ) : (
      <Loader />
    )
  }

  private renderIntegrationsSetUp(): JSX.Element {
    const { loading } = this.appState
    const isLoading =
      integrationsPageRelatedEvents.some(event => loading.get(event)) ||
      this.appState.isLoading

    return isLoading ? (
      <Loader />
    ) : (
      <IntegrationsSetUp
        initProjectId={this.appState.activeProject.id}
        projectSetUpPageStore={this.store}
      />
    )
  }

  public get isAdmin(): boolean {
    const { userActiveProjectSettings } = this.props.eventsStore.appState

    return userActiveProjectSettings?.isAdmin
  }

  public get isSidebarCollapsed(): boolean {
    const { isSettingsSidebarCollapsed, isSharedSidebarCollapsed } = this.store

    return this.props.sidebarOnly
      ? isSharedSidebarCollapsed
      : isSettingsSidebarCollapsed
  }
}
